'use client';

import {PepIcon} from '#/features/components/PepIcon';
import {useCurrentThemeColors} from '#/features/store/themeColorsStore';
import {type PepIconName} from '@calvium/pep-icons';
import classNames from 'classnames';
import Image from 'next/image';
import {useCallback, useMemo, useState} from 'react';
import Lightbox, {useController} from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/styles.css';

type LightBoxPepIconProps = {
  iconName: PepIconName;
  onClick?: () => void;
  className?: string;
};

const LightBoxPepIconButton = (props: LightBoxPepIconProps) => {
  const {iconName, onClick, className} = props;
  return (
    <button className={classNames('rounded-full bg-primary p-2 hover:bg-secondary', className)} onClick={onClick}>
      <PepIcon size="w-4" strokeColor="background" iconName={iconName} />
    </button>
  );
};

const LightBoxPepIconButtonClose = () => {
  const {close} = useController();
  return <LightBoxPepIconButton iconName="Remove" className={'absolute right-4'} onClick={close} />;
};

const LightBoxPepIconButtonNext = () => {
  const {next} = useController();
  return <LightBoxPepIconButton iconName="ArrowRight" className={'absolute right-4'} onClick={next} />;
};

const LightBoxPepIconButtonPrev = () => {
  const {prev} = useController();
  return <LightBoxPepIconButton iconName="ArrowLeft" className={'absolute left-4'} onClick={prev} />;
};

// const CurrentSlideLabel = ({numberOfImages}: {numberOfImages: number}) => {
//   const {currentIndex} = useLightboxState();
//   return (
//     <p className={'absolute left-0 right-0 w-full bg-danger text-center align-middle text-foreground'}>
//       {currentIndex + 1}/{numberOfImages}
//     </p>
//   );
// };

const maxImages: 1 | 2 | 3 = 3;

const imageClassNames = (index: number, imagesLength: number) => {
  const totalThree = imagesLength === 3;
  const totalTwo = imagesLength === 2;
  const totalOne = imagesLength === 1;
  const isFirst = index === 0;
  const isSecond = index === 1;
  const isThird = index === 2;
  const className = classNames(
    index === 0 && 'row-span-2',
    totalThree && isFirst && 'col-span-6 lg:col-span-8',
    totalThree && !isFirst && 'col-span-6  lg:col-span-4 col-start-7 lg:col-start-9',
    totalThree && isThird && 'row-start-2',
    totalTwo && 'col-span-6',
    totalTwo && isSecond && 'col-start-7',
    totalOne && 'col-span-12',
    !totalThree && 'row-span-2',
    'overflow-hidden',
    'relative'
  );
  return className;
};

type Props = {
  sources?: string[];
  alts?: string[];
};

export const CompositeHeroImage = (props: Props) => {
  const {sources, alts} = props;
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const themeColors = useCurrentThemeColors();

  const images = useMemo(
    () =>
      sources
        ?.map((url, index) => ({
          src: url,
          alt: alts?.[index] ?? '',
          title: `${index + 1}/${sources?.length ?? 1}`,
        }))
        .slice(0, maxImages) ?? undefined,
    [sources, alts]
  );

  const openLightbox = useCallback(() => {
    setOpen(true);
  }, []);

  const closeLightbox = useCallback(() => {
    setOpen(false);
  }, []);

  const handleImageClick = useCallback(
    (index: number) => {
      setIndex(index);
      openLightbox();
    },
    [openLightbox]
  );

  if (!images?.length) return null;

  return (
    <div className="grid h-72 w-full grid-cols-12 grid-rows-2 gap-1 overflow-hidden rounded-3xl">
      {images.map((image, index) => (
        <div key={image.src} className={imageClassNames(index, images.length)}>
          <Image
            className="cursor-pointer object-cover hover:brightness-75"
            src={image.src}
            alt={image.alt}
            onClick={() => handleImageClick(index)}
            fill
          />
        </div>
      ))}
      <Lightbox
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: true,
        }}
        plugins={[Captions]}
        captions={{
          descriptionMaxLines: 1,
          descriptionTextAlign: 'center',
          showToggle: false,
        }}
        index={index}
        open={open}
        close={closeLightbox}
        carousel={{
          finite: images.length <= 1,
        }}
        slides={images}
        render={{
          buttonPrev: () => (images?.length > 1 ? <LightBoxPepIconButtonPrev /> : null),
          buttonNext: () => (images?.length > 1 ? <LightBoxPepIconButtonNext /> : null),
          buttonClose: () => <LightBoxPepIconButtonClose />,
        }}
        toolbar={{
          buttons: ['close'],
        }}
        styles={{
          root: {
            '--yarl__color_backdrop': themeColors.background ?? 'black',
          },
          captionsTitleContainer: {
            '--yarl__slide_captions_container_background': themeColors.background ?? 'black',
            // backgroundColor: themeColors.background ?? 'black',
          },
          captionsTitle: {
            color: themeColors.foreground ?? 'white',
            fontSize: '1rem',
            textAlign: 'center',
          },
        }}
      />
    </div>
  );
};
